import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, BookOpen, Printer, Clock, Save } from 'lucide-react';
import { toast } from 'sonner';
import AIPracticeScenario from '../shared/AIPracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function PastInPresent() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };
  const [reflection, setReflection] = useState(() => {
    const saved = localStorage.getItem('pastInPresentReflection');
    return saved ? JSON.parse(saved) : { situation: '', reminder: '', oldHurt: '', feelingsNeeds: '', presentFeelings: '', request: '' };
  });

  const saveProgress = () => {
    localStorage.setItem('pastInPresentReflection', JSON.stringify(reflection));
    toast.success('Reflection saved!');
  };

  useEffect(() => {
    const interval = setInterval(() => localStorage.setItem('pastInPresentReflection', JSON.stringify(reflection)), 30000);
    return () => clearInterval(interval);
  }, [reflection]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
              <div><h1 className="text-2xl font-bold text-white">When Your Past Shows Up in the Present</h1></div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={saveProgress} variant="outline" size="sm" className="border-green-600 hover:bg-green-900/20 text-white"><Save className="h-4 w-4 mr-2" />Save</Button>
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white"><Printer className="h-4 w-4 mr-2" />Print</Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Pages 75-76</span></div>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8"><Clock className="h-16 w-16 text-amber-400 mx-auto mb-4" /><h2 className="text-3xl font-bold text-white mb-2">When Your Past Shows Up in the Present</h2><p className="text-slate-400 text-sm">LaShelle Charde's Connection Gem of the Week</p></div>
          <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-6 mb-8">
            <p className="text-amber-100 text-center text-xl font-semibold italic">"If it's hysterical, it's probably historical."</p>
          </div>
          <div className="space-y-6 text-slate-200 leading-relaxed mb-8">
            <p>As you know sometimes an old hurt gets triggered even though it doesn't have much to do with the present. A student of mine recently shared a motto she has that helps her remember this: "If it's hysterical, it's probably historical."</p>
            <p>This helps her to slow down when she is having a strong reaction and ask herself questions like: Has something like this happened before? Does this remind me of another time in my life? What am I telling myself about what just happened?</p>
            <p>After naming that your past is showing in the present, there are two important steps to take.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-green-200 mb-4">Step 1: Address the Old Hurt</h3>
              <p className="text-green-100 leading-relaxed">Connect with your feelings and needs around the old stuff. Then let that part of you know that you are not in the old situation anymore. You can talk to the old hurt like it is another you.</p>
            </div>
            <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-purple-200 mb-4">Step 2: Address the Present</h3>
              <p className="text-purple-100 leading-relaxed">Once you have honored the past, honor the present. You likely have feelings, needs, and a request relevant to the situation at hand.</p>
            </div>
          </div>
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-6 mb-8">
            <h3 className="text-xl font-bold text-white mb-4">Practice Reflection</h3>
            <div className="space-y-4">
              <div>
                <label className="text-slate-300 text-sm mb-2 block">A situation where you were recently triggered:</label>
                <Textarea value={reflection.situation} onChange={(e) => setReflection({...reflection, situation: e.target.value})} placeholder="Describe the situation..." className="bg-slate-900/40 border-slate-700 text-white min-h-[80px]" />
              </div>
              <div>
                <label className="text-amber-300 text-sm mb-2 block">What did this situation remind you of?</label>
                <Textarea value={reflection.reminder} onChange={(e) => setReflection({...reflection, reminder: e.target.value})} placeholder="Does it remind you of anything from your past?" className="bg-amber-900/20 border-amber-700/30 text-white min-h-[80px]" />
              </div>
              <div>
                <label className="text-green-300 text-sm mb-2 block">Name the old hurt and the feelings/needs associated with it:</label>
                <Textarea value={reflection.oldHurt} onChange={(e) => setReflection({...reflection, oldHurt: e.target.value})} placeholder="The old hurt and feelings/needs..." className="bg-green-900/20 border-green-700/30 text-white min-h-[80px]" />
              </div>
              <div>
                <label className="text-purple-300 text-sm mb-2 block">Feelings/needs based just on the present situation:</label>
                <Textarea value={reflection.presentFeelings} onChange={(e) => setReflection({...reflection, presentFeelings: e.target.value})} placeholder="What's alive in you NOW?" className="bg-purple-900/20 border-purple-700/30 text-white min-h-[80px]" />
              </div>
              <div>
                <label className="text-blue-300 text-sm mb-2 block">A request you might have made (or still can make):</label>
                <Textarea value={reflection.request} onChange={(e) => setReflection({...reflection, request: e.target.value})} placeholder="Your request..." className="bg-blue-900/20 border-blue-700/30 text-white min-h-[80px]" />
              </div>
            </div>
          </div>
          <p className="text-center text-slate-400 text-sm mb-6">💾 Auto-saves every 30 seconds</p>
          <p className="text-slate-400 text-sm text-center mb-8">LaShelle Charde's Connection Gem of the Week • www.wiseheartpdx.org</p>
          <AIPracticeScenario pageTitle="When Your Past Shows Up in the Present" pageNumber={75} />
          <PageMessaging pageTitle="When Your Past Shows Up in the Present" pageNumber={75} />
        </div>
      </div>
    </div>
  );
}
